import { Box, Container, Grid, Typography, useMediaQuery } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import theme from "../../styles/theme";
import { useTranslation } from "react-i18next";

import aboutImage from "../../assets/images/founder.png";

const AboutSection = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const highlights = [
    t(
      "Home.About.Points.1",
      "Over 20 års erfaring i trykk- og grafisk bransje",
    ),
    t(
      "Home.About.Points.2",
      "Spesialisert på digital DTF-trykk (Direct to Film)",
    ),
    t("Home.About.Points.3", "Sterke farger, høy detaljgrad og lang levetid"),
    t("Home.About.Points.4", "Fleksibelt for både små og store opplag"),
    t("Home.About.Points.5", "Personlig service og individuell oppfølging"),
    t("Home.About.Points.6", "Kvalitet, presisjon og profesjonelle løsninger"),
  ];

  return (
    <Box
      id="about"
      backgroundColor="background.default2"
      sx={{ py: { xs: 6, md: 10 } }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
          {/* Image */}
          <Grid data-aos="fade-up" size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                position: "relative",
                borderRadius: 6,
                overflow: "hidden",
                boxShadow: "0 16px 50px rgba(0,0,0,0.14)",
                backgroundColor: "#fff",
              }}
            >
              <Box
                component="img"
                src={aboutImage}
                loading="lazy"
                alt={t("Home.About.ImageAlt", "Om oss")}
                sx={{
                  width: "100%",
                  height: { xs: 460, md: 820 },
                  objectFit: "cover",
                  objectPosition: { xs: "50% 15%", md: "50% 35%" },
                  display: "block",
                }}
              />

              {/* 20+ badge */}
              <Box
                sx={{
                  position: "absolute",
                  right: 18,
                  bottom: 18,
                  width: 92,
                  height: 92,
                  borderRadius: 4,
                  backgroundColor: "secondary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 14px 36px rgba(0,0,0,0.18)",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={900}
                  sx={{ color: "rgba(0,0,0,0.9)" }}
                >
                  20+
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Content */}
          <Grid data-aos="fade-up" data-aos-delay={300} size={{ xs: 12, md: 6 }}>
            <Box sx={{ pl: { md: 2 }, textAlign: "left" }}>
              <Typography
                variant="h3"
                component="h2"
                fontWeight={800}
                sx={{ mb: 2, lineHeight: 1.1 }}
              >
                {t("Home.About.Title.Before", "Om")}{" "}
                <Box component="span" sx={{ color: "secondary.dark" }}>
                  {t("Home.About.Title.Highlight", "Oss")}
                </Box>
              </Typography>

              <Typography
                variant="body1"
                fontSize={18}
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {t(
                  "Home.About.Paragraph1",
                  "Vi er en bedrift spesialisert på digital DTF-trykk (Direct to Film), og tilbyr trykkløsninger av høy kvalitet for klær og tekstiler. Med over 20 års erfaring i trykk- og grafisk bransje hjelper vi bedrifter, merkevarer og privatpersoner med å realisere sine ideer gjennom profesjonelt og holdbart trykk.",
                )}
              </Typography>

              <Typography
                variant="body1"
                fontSize={18}
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {t(
                  "Home.About.Paragraph2",
                  "Ved hjelp av moderne teknologi og nøye utvalgte materialer leverer vi DTF-trykk med sterke farger, høy detaljgrad og lang levetid. Denne metoden gir stor fleksibilitet og egner seg perfekt for både små og store opplag, samt ulike typer tekstiler.",
                )}
              </Typography>

              <Typography
                variant="body1"
                fontSize={18}
                color="text.secondary"
                sx={{ mb: 6 }}
              >
                {t(
                  "Home.About.Paragraph3",
                  "Vi legger stor vekt på kvalitet, presisjon og personlig service. Hvert prosjekt behandles individuelt, og vi følger kunden gjennom hele prosessen for å sikre et resultat som møter forventningene. For oss handler trykk ikke bare om produksjon, men om å skape synlighet og verdi gjennom profesjonelle løsninger.",
                )}
              </Typography>

              {/* Bullet points */}
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  display: "grid",
                  gap: 1.6,
                }}
              >
                {highlights.map((text, i) => (
                  <Box
                    key={text}
                    component="li"
                    data-aos="fade-up"
                    data-aos-delay={i * 70}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        mt: "2px",
                        color: "secondary.dark",
                        fontSize: 22,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body1" color="text.main">
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Small spacing on mobile */}
              {isMobile ? <Box sx={{ mt: 1 }} /> : null}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutSection;
